import snakeCase from "lodash/snakeCase";
import {
  History,
  Mail,
  MessageSquare,
  Phone,
  Server,
  StickyNote,
  Tag,
} from "lucide-react";

import {
  operandsOf,
  readEnvelope,
} from "components/NewAdvancedSearch/envelope";

/**
 * Activity Log quick filters. A chip is a shortcut for event types the Filters
 * panel also offers, and it writes them in the panel's own format, so the two
 * controls always agree about what is selected.
 *
 * A chip names whole registry groups when every event in the group belongs to
 * it, so an event later added to that group is picked up. It names the
 * matching bucket as well, since the Case, Revenue Opportunity, Contact and
 * Entity feeds offer buckets instead of groups. Tags and machines share their
 * groups with unrelated events and name their events. Machine tag changes stay
 * with the Tags chip, so those two chips share no event.
 */
export const QUICK_FILTERS = [
  {
    key: "notes",
    label: "Notes",
    icon: StickyNote,
    groups: ["note_events", "notes"],
  },
  {
    key: "calls",
    label: "Calls",
    icon: Phone,
    groups: ["call_events", "calls"],
  },
  {
    key: "emails",
    label: "Emails",
    icon: Mail,
    groups: ["email_events", "emails"],
  },
  {
    key: "messages",
    label: "Messages",
    icon: MessageSquare,
    groups: ["message_events", "text_messages"],
  },
  {
    key: "tags",
    label: "Tags",
    icon: Tag,
    events: [
      "contact_tag_changed",
      "entity_tag_changed",
      "case_tag_changed",
      "revenue_opportunity_tag_changed",
      "machine_tag_changed",
      "tag_metadata_changed",
    ],
  },
  {
    key: "machines",
    label: "Machines",
    icon: Server,
    events: [
      "machine_created",
      "machine_status_changed",
      "machine_transferred_in",
      "machine_transferred_out",
    ],
  },
];

// Keyed by the snake_case model type, whose `{model}_changes` bucket holds
// every change to the record, custom field value edits included.
const RECORD_CHANGES_LABELS = {
  entity: "Entity changes",
  contact: "Contact changes",
  case: "Case changes",
  revenue_opportunity: "Revenue Opportunity changes",
};

/**
 * The chips for a model's Activity Log: the shared ones, plus a chip for the
 * record's own changes when the model has a changes bucket. The label comes
 * from the model type so the placeholder shown while options load carries it.
 */
export function quickFiltersFor(modelType) {
  const model = snakeCase(modelType);
  const label = RECORD_CHANGES_LABELS[model];
  if (!label) return QUICK_FILTERS;
  return [
    ...QUICK_FILTERS,
    {
      key: "record_changes",
      label,
      icon: History,
      groups: [`${model}_changes`],
    },
  ];
}

const optionIds = descriptor =>
  (descriptor.data ?? []).map(option => option.id);

/**
 * The chip's event types that this record's Activity Log can contain, as
 * `{ group, event }` pairs. Empty when the record can produce none of them.
 */
export function chipEvents(chip, filters) {
  return filters.flatMap(descriptor =>
    optionIds(descriptor)
      .filter(id =>
        chip.groups
          ? chip.groups.includes(descriptor.key)
          : chip.events.includes(id)
      )
      .map(event => ({ group: descriptor.key, event }))
  );
}

// Reads a group's selection in either wire format the panel accepts: the
// { op, value } envelope it writes, or the CSV an older bookmark carries.
function readGroupIds(raw) {
  if (raw == null) return [];
  const clauses = readEnvelope(raw);
  if (clauses?.length) return operandsOf(clauses[0]);
  return String(raw).split(",").filter(Boolean);
}

export function isChipActive(events, queryParams) {
  return (
    events.length > 0 &&
    events.every(({ group, event }) =>
      readGroupIds(queryParams[group]).includes(event)
    )
  );
}

const panelOwned = (queryParams, filterKeys) =>
  Object.fromEntries(
    Object.entries(queryParams).filter(([key]) => filterKeys.includes(key))
  );

// Rewrites each group the chip touches, leaving every other panel param as
// the URL holds it. A group left with nothing selected is dropped, because
// the backend rejects an envelope with no operands.
function rewriteGroups(events, queryParams, filterKeys, updateIds) {
  const next = panelOwned(queryParams, filterKeys);
  const groups = [...new Set(events.map(({ group }) => group))];
  groups.forEach(group => {
    const chipIds = events
      .filter(pair => pair.group === group)
      .map(({ event }) => event);
    const ids = updateIds(readGroupIds(queryParams[group]), chipIds);
    if (ids.length) {
      next[group] = JSON.stringify({ op: "in", value: ids });
    } else {
      delete next[group];
    }
  });
  return next;
}

/** The panel-owned params with the chip's event types added. */
export function addChip(events, queryParams, filterKeys) {
  return rewriteGroups(events, queryParams, filterKeys, (current, chipIds) => [
    ...current,
    ...chipIds.filter(id => !current.includes(id)),
  ]);
}

/** The panel-owned params with only the chip's event types removed. */
export function removeChip(events, queryParams, filterKeys) {
  return rewriteGroups(events, queryParams, filterKeys, (current, chipIds) =>
    current.filter(id => !chipIds.includes(id))
  );
}
